import { BetRoomSeatIndex } from '@/models/games/BetRoom';
import {
    BlackjackBetContent,
    BlackjackBetType,
    BlackjackDecision,
    BlackjackResult,
} from '@/models/games/blackjack';
import { BetType } from '@/models/games/blackjack/enums/BetType';
import { Blackjack2BetType } from '@/models/games/blackjack2';
import { WinState } from '@/models/games/blackjack2/enums/WinState';
import { ResultInfo } from '@/models/games/blackjack2/GameInfo';
import { GameType as GameTypeEnum } from '@/models/games/enums/GameType';
import { getMainUserState } from '@/modules/main/selector';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

export const GetGameName = (
    gameType: number,
    hostId: number,
    t: TFunction
): string => {
    switch (gameType) {
        //TODO: other game
        case GameTypeEnum.Baccarat:
        case GameTypeEnum.SqueezeBaccarat:
            return (
                t('baccarat.gametype') + ' - ' + t(`hostCodeNames.${hostId}`)
            );
        default:
            return t(`hostList.${hostId}`);
    }
};

export const GetGameShoeRound = (
    gameType: number,
    round: number,
    shoes: number
): string => {
    switch (gameType) {
        case GameTypeEnum.SeDie:
        case GameTypeEnum.TeenPatti2020:
        case GameTypeEnum.AndarBahar:
        case GameTypeEnum.Roulette:
        case GameTypeEnum.UltraRoulette:
        case GameTypeEnum.SicBo:
        case GameTypeEnum.ThaiHiLo:
        case GameTypeEnum.FishPrawnCrab:
        case GameTypeEnum.CarnivalTreasure:
            return round.toString();
        default:
            return `${shoes}-${round}`;
    }
};

export const GetBetTypeToString = (
    gameType: number,
    betType: number,
    result: string
) => {
    const { t } = useTranslation();
    const { PlayerID } = useSelector(getMainUserState);

    switch (gameType) {
        case GameTypeEnum.Baccarat:
        case GameTypeEnum.SqueezeBaccarat:
            return t(`betlog.bet_bacc.${betType}`);
        case GameTypeEnum.SeDie:
            return t(`betlog.bet_sedie.${betType}`);
        case GameTypeEnum.Dragon:
            return t(`betlog.bet_dt.${betType}`);
        case GameTypeEnum.PokDeng:
            return t(`betlog.bet_pokdeng.${betType}`);
        case GameTypeEnum.SicBo:
            return t(`betlog.bet_sicbo.${betType}`);
        case GameTypeEnum.TeenPatti2020:
            return t(`betlog.bet_teenpatti.${betType}`);
        case GameTypeEnum.AndarBahar:
            return t(`betlog.bet_andarbahar.${betType}`);
        case GameTypeEnum.UltraRoulette:
        case GameTypeEnum.Roulette:
            return t(`betlog.bet_roulette.${betType}`);
        case GameTypeEnum.ThaiHiLo:
            return t(`betlog.bet_thaihilo.${betType}`);
        case GameTypeEnum.FishPrawnCrab:
            return t(`betlog.bet_fishprawncrab.${betType}`);
        case GameTypeEnum.CarnivalTreasure:
            return t(`betlog.bet_carnival_treasure.${betType}`);
        case GameTypeEnum.Blackjack:
            if (betType === BetType.BJB_Insurance) {
                return t(
                    `betlog.bet_blackjack.${getBJBetContent(result, betType, PlayerID)}`
                );
            } else {
                return `${seatName[betType % 7]} - ${t(
                    `betlog.bet_blackjack.${getBJBetContent(result, betType, PlayerID)}`
                )}`;
            }
        case GameTypeEnum.Blackjack2:
            return `${seatName2[betType % 10]} - ${t(
                `betlog.bet_blackjack2.${getBJ2BetContent(result, betType)}`
            )}`;
        default:
            return '-';
    }
};

const seatName = ['P1', 'P2', 'P3', 'P5', 'P6', 'P7', 'P8'];
const seatName2 = ['', 'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7'];
const getBJBetContent = (result: string, betType: number, playerID: number) => {
    if (betType === BlackjackBetType.BJB_Insurance) {
        return BlackjackBetContent.Insurance;
    } else {
        const blackjackResult = JSON.parse(result) as BlackjackResult;
        const seat = ((betType % 7) + 1) as BetRoomSeatIndex;
        const playerResult = blackjackResult.P.find(p => p.SN === seat);
        if (playerResult) {
            const isSideBet = playerResult.I !== playerID.toString();
            const decision = playerResult.D as BlackjackDecision;
            switch (Math.floor(betType / 7)) {
                case 0: //split1
                    if (decision === BlackjackDecision.Surrender)
                        return isSideBet
                            ? BlackjackBetContent.BetBehindSurrender
                            : BlackjackBetContent.SitInSurrender;
                    else
                        return isSideBet
                            ? BlackjackBetContent.BetBehind
                            : BlackjackBetContent.SitInBet;
                case 1: //split2
                    return isSideBet
                        ? BlackjackBetContent.BetBehindSplit
                        : BlackjackBetContent.SitInSplit;

                case 2: //double down
                    return isSideBet
                        ? BlackjackBetContent.BetBehindDoubleDown
                        : BlackjackBetContent.SitInDoubleDown;

                case 3: //pair
                    return isSideBet
                        ? BlackjackBetContent.BetBehindPair
                        : BlackjackBetContent.SitInPair;

                case 4: //insurance
                    return BlackjackBetContent.Insurance;
            }
        }
    }
};

const getBJ2BetContent = (result: string, betType: number) => {
    // console.log('????', result, betType);
    const resultInfo =
        result == '' ? undefined : (JSON.parse(result) as ResultInfo);
    const seat = betType % 10;
    const bet = betType - seat;
    const seatResult = resultInfo
        ? resultInfo.seats_info.find(v => v.seat_num == seat)
        : undefined;
    switch (bet) {
        case Blackjack2BetType.BJB_Split1_Start:
            if (seatResult?.h1.r == WinState.Surrender) {
                return BlackjackBetContent.SitInSurrender;
            } else {
                return BlackjackBetContent.SitInBet;
            }
        case Blackjack2BetType.BJBB_Split1_Start:
            if (seatResult?.h1.r == WinState.Surrender) {
                return BlackjackBetContent.BetBehindSurrender;
            } else {
                return BlackjackBetContent.BetBehind;
            }
        case Blackjack2BetType.BJB_Pair_Start:
            return BlackjackBetContent.SitInPair;
        // return BlackjackBetContent.BetBehindPair;
        case Blackjack2BetType.BJB_Split2_Start:
            return BlackjackBetContent.SitInSplit;
        case Blackjack2BetType.BJB_DoubleDown_H1_Start:
            if (seatResult?.is_split) {
                return BlackjackBetContent.SitInDoubleDownH1;
            } else {
                return BlackjackBetContent.SitInDoubleDown;
            }
        case Blackjack2BetType.BJB_DoubleDown_H2_Start:
            return BlackjackBetContent.SitInDoubleDownH2;
        case Blackjack2BetType.BJBB_Split2_Start:
            return BlackjackBetContent.BetBehindSplit;
        case Blackjack2BetType.BJBB_DoubleDown_H1_Start:
            if (seatResult?.is_split) {
                return BlackjackBetContent.BetBehindDoubleDownH1;
            } else {
                return BlackjackBetContent.BetBehindDoubleDown;
            }
        case Blackjack2BetType.BJBB_DoubleDown_H2_Start:
            return BlackjackBetContent.BetBehindDoubleDownH2;
        case Blackjack2BetType.BJB_Insurant_Start:
            return BlackjackBetContent.Insurance;
        case Blackjack2BetType.BJBB_Insurant_Start:
            return BlackjackBetContent.BetBehindInsurance;
    }
};
